import type { GameSide } from '../chatv1/transport'

/**
 * The xiangqi board's geometry, apart from any drawing so it can be tested: points by name ("e0": a file a–i from
 * Red's left, a rank 0–9 from Red's side — the service's own names, chat lambda/xiangqi.ts), the pieces a position
 * holds, and where a point is on the 3D board. The app keeps no copy of the rules: where a piece may go comes from
 * the service (the view's `legal`), and a move is only ever shown here, never judged.
 */

export const FILES = 9
export const RANKS = 10
const LETTERS = 'abcdefghi'
const POINT = /^([a-i])([0-9])$/

export type PieceType = 'k' | 'a' | 'b' | 'n' | 'r' | 'c' | 'p'
export type Piece = { type: PieceType; side: GameSide }

/** [file, rank], both from 0, or null. */
export function parsePoint(p: string): [number, number] | null {
  const m = POINT.exec(p)
  return m ? [LETTERS.indexOf(m[1]!), Number(m[2])] : null
}

export function pointName(file: number, rank: number): string {
  return `${LETTERS[file]}${rank}`
}

/** The pieces of a position (xiangqi FEN, Black's back rank first; Red's pieces in capitals), by point. */
export function piecesOf(fen: string): Map<string, Piece> {
  const out = new Map<string, Piece>()
  const rows = (fen.split(' ')[0] ?? '').split('/')
  rows.forEach((row, i) => {
    const rank = RANKS - 1 - i
    let file = 0
    for (const ch of row) {
      if (/\d/.test(ch)) {
        file += Number(ch)
        continue
      }
      const type = ch.toLowerCase()
      if ('kabnrcp'.includes(type) && file < FILES) {
        out.set(pointName(file, rank), { type: type as PieceType, side: ch === type ? 'b' : 'w' })
      }
      file++
    }
  })
  return out
}

function fenOf(pieces: Map<string, Piece>, turn: GameSide): string {
  const rows: string[] = []
  for (let rank = RANKS - 1; rank >= 0; rank--) {
    let row = ''
    let gap = 0
    for (let file = 0; file < FILES; file++) {
      const p = pieces.get(pointName(file, rank))
      if (!p) {
        gap++
        continue
      }
      row += (gap ? String(gap) : '') + (p.side === 'w' ? p.type.toUpperCase() : p.type)
      gap = 0
    }
    rows.push(row + (gap ? String(gap) : ''))
  }
  return `${rows.join('/')} ${turn}`
}

/** The position after a piece goes from one point to another (taking what stood there) — for showing a move at once. */
export function afterMove(fen: string, from: string, to: string): string {
  const pieces = piecesOf(fen)
  const p = pieces.get(from)
  if (!p) return fen
  pieces.delete(from)
  pieces.set(to, p)
  return fenOf(pieces, p.side === 'w' ? 'b' : 'w')
}

/** The traditional characters: Red's 帥仕相傌俥炮兵, Black's 將士象馬車砲卒. */
const HAN: Record<GameSide, Record<PieceType, string>> = {
  w: { k: '帥', a: '仕', b: '相', n: '傌', r: '俥', c: '炮', p: '兵' },
  b: { k: '將', a: '士', b: '象', n: '馬', r: '車', c: '砲', p: '卒' },
}
export const glyphOf = (p: Piece): string => HAN[p.side][p.type]

const NAME: Record<PieceType, string> = {
  k: 'general',
  a: 'advisor',
  b: 'elephant',
  n: 'horse',
  r: 'chariot',
  c: 'cannon',
  p: 'soldier',
}
export const nameOf = (p: Piece): string => `${p.side === 'w' ? 'red' : 'black'} ${NAME[p.type]}`

/** A file as a side counts it in WXF: from its own right, 1 to 9 (Red's i-file is its 1, Black's a-file its 1). */
export function fileNumber(file: number, side: GameSide): number {
  return side === 'w' ? FILES - file : file + 1
}

/** The points a piece on the board's corner marks stand on: where the cannons and soldiers start. */
export const MARKED: readonly string[] = ['b2', 'h2', 'b7', 'h7', 'a3', 'c3', 'e3', 'g3', 'i3', 'a6', 'c6', 'e6', 'g6', 'i6']

/**
 * 3D: one cell = 1 world unit, the board in the x–z plane centred on the origin; Red's back rank nearest a camera on
 * Red's side (+z), the a-file on the left (−x).
 */
export function pointToWorld(p: string): [number, number] | null {
  const at = parsePoint(p)
  if (!at) return null
  return [at[0] - (FILES - 1) / 2, (RANKS - 1) / 2 - at[1]]
}

/** The point under a spot on the board, when the spot is near enough to it to mean it; else null. */
export function worldToPoint(x: number, z: number): string | null {
  const file = Math.round(x + (FILES - 1) / 2)
  const rank = Math.round((RANKS - 1) / 2 - z)
  if (file < 0 || file >= FILES || rank < 0 || rank >= RANKS) return null
  const [wx, wz] = pointToWorld(pointName(file, rank))!
  if (Math.hypot(x - wx, z - wz) > 0.5) return null
  return pointName(file, rank)
}

/** Where a piece on `from` may go, from the service's list of the moves ("h2e2"). */
export function targetsFrom(legal: readonly string[], from: string): Set<string> {
  return new Set(legal.filter((m) => m.startsWith(from)).map((m) => m.slice(2)))
}
